import type { MedplumClient } from '@medplum/core'
import type {
  Bundle,
  DocumentReference,
  Practitioner,
  Provenance,
  Reference,
} from '@medplum/fhirtypes'
import { ATTESTATION_ACTIVITY_CODE } from '@/model/middleware-types'

/** Who signed a note and when — the electronic signature (PD-1897). */
export type NoteSignature = {
  signedAt: string
  signer: Reference<Practitioner>
}

// Mirrors SIGNED_DOC_STATUSES in packages/middleware/src/visit/signed-note-guard.ts.
const SIGNED_DOC_STATUSES = new Set<DocumentReference['docStatus']>(['final', 'amended'])

function asPractitionerReference(
  reference: Reference | undefined,
): Reference<Practitioner> | undefined {
  return reference?.reference?.startsWith('Practitioner/')
    ? (reference as Reference<Practitioner>)
    : undefined
}

/**
 * The note's signature from its attestation Provenance (activity `AU`), which
 * the lock and the addendum signature write once and never update. The
 * earliest one wins: it is the moment the note became signed.
 */
export function findNoteAttestation(
  provenances: Provenance[],
  documentReference: string,
): NoteSignature | undefined {
  const attestation = ownProvenances(provenances, documentReference)
    .filter(
      (provenance) =>
        provenance.target?.some((target) => target.reference === documentReference) &&
        provenance.activity?.coding?.some((coding) => coding.code === ATTESTATION_ACTIVITY_CODE),
    )
    .sort((a, b) => (a.recorded ?? '').localeCompare(b.recorded ?? ''))[0]
  const signedAt = attestation?.signature?.[0]?.when ?? attestation?.recorded
  const signer = asPractitionerReference(
    attestation?.agent?.find((agent) => agent.type?.coding?.some((c) => c.code === 'verifier'))
      ?.who ?? attestation?.signature?.[0]?.who,
  )
  return signedAt && signer ? { signedAt, signer } : undefined
}

/**
 * The signature of a signed note: its attestation Provenance, else — for notes
 * locked before the lock wrote one, which were created already `final` — the
 * first signed version in the note's history. Unsigned notes have none.
 */
export async function resolveNoteSignature(
  medplum: MedplumClient,
  documentReference: DocumentReference,
  provenances: Provenance[],
): Promise<NoteSignature | undefined> {
  if (!documentReference.id || !SIGNED_DOC_STATUSES.has(documentReference.docStatus)) {
    return undefined
  }
  const attested = findNoteAttestation(provenances, `DocumentReference/${documentReference.id}`)
  if (attested) return attested

  // History pages run newest first, so the first signed version can sit on any page.
  const versions: DocumentReference[] = []
  let page: Bundle<DocumentReference> | undefined = await medplum.readHistory(
    'DocumentReference',
    documentReference.id,
  )
  while (page) {
    versions.push(
      ...(page.entry ?? []).flatMap((entry) => (entry.resource ? [entry.resource] : [])),
    )
    const next: string | undefined = page.link?.find((link) => link.relation === 'next')?.url
    page = next ? await medplum.get<Bundle<DocumentReference>>(next) : undefined
  }
  const firstSigned = versions
    .filter((version) => SIGNED_DOC_STATUSES.has(version.docStatus))
    .sort((a, b) => (a.meta?.lastUpdated ?? '').localeCompare(b.meta?.lastUpdated ?? ''))[0]
  const signedAt = firstSigned?.meta?.lastUpdated
  const signer = asPractitionerReference(firstSigned?.authenticator ?? firstSigned?.meta?.author)
  return signedAt && signer ? { signedAt, signer } : undefined
}

/**
 * The Provenances that describe THIS document, dropping any that merely
 * consumed it.
 *
 * PD-1491 — `save-and-notify` writes the Patient Instructions Provenance with
 * the originating note in `target` AND in `entity[{role:'source'}]`, and its
 * agents name whoever generated the instructions. Unioning that in made a
 * locked note whose own record names only a `verifier` report the instructions
 * author as its Source and Provider, which is exactly the invented attribution
 * the read path must never make. A Provenance that names this document as its
 * `source` entity is about a document derived FROM it, so its agents are not
 * this document's.
 *
 * Keyed on `entity`, deliberately not on `target[0]`: the visit cascade's
 * Provenance lists the QuestionnaireResponse first and the note second, so a
 * target-position rule would discard the one Provenance carrying the real
 * source and performer. The visit cascade and the Claim link both name an
 * `Encounter` in `entity`, so both are correctly kept.
 */
export function ownProvenances(provenances: Provenance[], documentReference: string): Provenance[] {
  return provenances.filter(
    (provenance) =>
      !provenance.entity?.some(
        (entity) => entity.role === 'source' && entity.what?.reference === documentReference,
      ),
  )
}

/**
 * Collapse every Provenance describing one document into a single Provenance
 * carrying ALL of their agents.
 *
 * PD-1491: a note accumulates several Provenances over its life — autosave
 * mints one per save, `materializeClinical` adds one, and the progress-note
 * lock adds one whose ONLY agent is `verifier`. Readers that picked a single
 * Provenance (`.find(...)`, or `[0]`) silently dropped every agent living on a
 * sibling, so the Source and Provider rows rendered '-' depending on which
 * Provenance the search happened to return first.
 *
 * Returning one Provenance (rather than widening every reader to a list) keeps
 * the ~19 downstream `agent?.find((a) => ...code === 'source')` lookups and
 * `getDisplayData`'s signature untouched.
 *
 * `documentReference` is required, not optional: the union is only correct over
 * the Provenances that describe this document, and a caller that cannot say
 * which document it is asking about cannot make that distinction — see
 * {@link ownProvenances}.
 *
 * Agents are ordered most-recently-`recorded` first, so those `.find()` lookups
 * take the latest value when two Provenances genuinely name different sources
 * or performers. Provenances with no `recorded` sort last; ties keep bundle
 * order (Array#sort is stable).
 *
 * ponytail: deliberately duplicated in packages/patient-web/src/utils/provenance.ts —
 * neither shared package can carry it (`@behuman.corp/contracts` needs a
 * publish round-trip, `@behuman/core-contract` only re-exports middleware
 * source, which the vite boundary plugin blocks), and a new workspace package
 * costs far more than these lines. Revisit if it grows past ~20 lines of logic
 * or a third frontend needs it.
 */
export function mergeProvenanceAgents(
  provenances: Provenance[],
  documentReference: string,
  base?: Provenance,
): Provenance | undefined {
  const own = ownProvenances(provenances, documentReference)
  const chosen = base ?? own[0]
  if (!chosen) return undefined

  const latestRecordedFirst = [...own].sort((a, b) =>
    (b.recorded ?? '').localeCompare(a.recorded ?? ''),
  )

  return { ...chosen, agent: latestRecordedFirst.flatMap((provenance) => provenance.agent ?? []) }
}

/**
 * Resolve the one Provenance the chart's document view renders from everything
 * `_revinclude=Provenance:target` returned for a DocumentReference.
 *
 * A note can have MULTIPLE Provenances (the SOAP save writes one targeting the
 * QuestionnaireResponse; another may target the Claim). Base off the QR-linked
 * one — it drives structured-mode and AI-source detection — but union every
 * Provenance's targets because `hasQuestionnaireResponseTarget` reads the
 * merged targets in Documents.tsx to gate `canEdit` and structured rendering.
 *
 * PD-1491 extends that same union to `agent`, which was still collapsing, and
 * scopes both the base and the agent union to the Provenances that describe
 * this document ({@link ownProvenances}).
 *
 * `target` stays unioned over EVERY Provenance in the bundle, including the
 * excluded ones. That is deliberate and unchanged: the union feeds
 * `hasQuestionnaireResponseTarget` in Documents.tsx, which gates `canEdit` and
 * the structured render.
 */
export function resolveDocumentProvenance(
  provenances: Provenance[],
  documentReference: string,
): Provenance | undefined {
  const own = ownProvenances(provenances, documentReference)
  const base = own.find((provenance) =>
    (provenance.target ?? []).some((target) =>
      target.reference?.startsWith('QuestionnaireResponse/'),
    ),
  )
  const merged = mergeProvenanceAgents(own, documentReference, base)

  return (
    merged && {
      ...merged,
      target: provenances.flatMap((provenance) => provenance.target ?? []),
    }
  )
}

/**
 * Source / Provider for the document summary. Each reads a single agent off the
 * Provenance, so the Provenance handed in must already carry every sibling's
 * agents — see {@link mergeProvenanceAgents}.
 *
 * '-' is the intended empty state and stays: when the record names no source or
 * performer we say so, rather than inventing one from the lock's `verifier`
 * agent or from `DocumentReference.authenticator`.
 */
export function getSource(prov?: Provenance) {
  const sourceAgent = prov?.agent?.find((a) => a.type?.coding?.some((c) => c.code === 'source'))
  return sourceAgent?.who?.display || '-'
}

export function getProvider(prov?: Provenance) {
  const performer = prov?.agent?.find((a) => a.type?.coding?.some((c) => c.code === 'performer'))
  return performer?.who?.display || '-'
}

export function getSourceReference(prov?: Provenance) {
  const sourceAgent = prov?.agent?.find((a) => a.type?.coding?.some((c) => c.code === 'source'))
  return sourceAgent?.who?.reference
}

export function getProviderReference(prov?: Provenance) {
  const performer = prov?.agent?.find((a) => a.type?.coding?.some((c) => c.code === 'performer'))
  return performer?.who?.reference
}
